import { PrescriptionType } from '~/constants/prescription'
import type {
  Prescription,
  PrescriptionV2,
} from '~/types/entities/prescriptions'
import { convertPrescriptionV2ToPrescription } from '~/utils/prescription'

import {
  type OrderTableRowInput,
  orderTableRowInputsToContent,
  refillsToOrderTableRowInputs,
  rxUsagesToOrderTableRowInputs,
  type RxUsagesWithShipmentsDataForConversion,
} from './orderTableConversions'
import type {
  OrderTableContent,
  OrderTableFieldRowOptions,
} from './orderTableTypes'
import { hasOrderTable } from './rxCardUtilities'

type NullablePrescriptionType = PrescriptionType | null | undefined

export interface GetOrderTableFieldsOptions extends OrderTableFieldRowOptions {
  isOrderedDrug?: boolean
  /** When set (e.g. after expanding order table), use so refills and full prescription are available for building rows when not using rxUsages. */
  orderTableLoadedRx?: PrescriptionV2 | null
  /** When set (Chewy) and rxUsages present, table is built from rxUsages instead of refills. */
  rxUsagesData?: RxUsagesWithShipmentsDataForConversion | null
  timezone: string
}

export function getOrderTableFields(
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  options: GetOrderTableFieldsOptions,
): OrderTableContent | null {
  if (!hasOrderTable(rx, prescriptionType)) return null
  const { orderTableLoadedRx, rxUsagesData, timezone, ...opts } = options
  const loadedSameAsRx =
    orderTableLoadedRx?.id != null && orderTableLoadedRx.id === rx.id
  const convertedLoadedRx =
    orderTableLoadedRx == null
      ? undefined
      : convertPrescriptionV2ToPrescription(orderTableLoadedRx)

  const useRxUsagesPath =
    prescriptionType === PrescriptionType.CHEWY &&
    rxUsagesData?.rxUsages != null &&
    rxUsagesData.rxUsages.length > 0

  let rowInputs: OrderTableRowInput[]

  if (useRxUsagesPath && rxUsagesData) {
    rowInputs = rxUsagesToOrderTableRowInputs(rx, rxUsagesData)
  } else {
    const rxForTable =
      loadedSameAsRx && orderTableLoadedRx ? orderTableLoadedRx : rx
    const refills = (rxForTable.refills ?? []).filter(
      (r): r is Prescription => r != null,
    )
    const rxForTableWithRefills = rxForTable as Prescription & {
      numberRefills?: number
      quantityNumbRefills?: number
    }
    rowInputs = refillsToOrderTableRowInputs(rx, refills, {
      quantityNumbRefills: rxForTableWithRefills.quantityNumbRefills,
      numberRefills: rxForTableWithRefills.numberRefills,
    })
  }

  if (rowInputs.length === 0) return null

  return orderTableRowInputsToContent(rowInputs, {
    ...opts,
    parentRx: rx,
    prescriptionType,
    timezone,
    orderTableLoadedRx: options.orderTableLoadedRx ?? null,
    convertedLoadedRx,
    loadedSameAsRx,
  })
}
